import { communicationStatus, Modal, Tooltip } from '@ui-kit'
import { useEffect, useRef, useState } from 'react'
import { AddDataModal } from '../../components/MultiLookupRowRender'
import { FieldValues } from 'react-hook-form'
import {
  completeTaskByTaskIdApi,
  DataTypeStore,
  DropdownType,
  hideLoader,
  interruptWorkFlow,
  showLoader,
  useDispatch,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  MISSING_INFO_POPUP_CONSTANTS,
  NAMESPACES,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import {
  DataType,
  OverallMiStatus,
} from '../../types/MissingInformationSummary.types'
import { useAppSelector } from '../../utils/agent.store'
import { useParams } from 'react-router-dom'
import {
  fetchMissingInformationData,
  getAllLovs,
  updateManualMissingFields,
  validateCase,
} from '../../apis/missingInformation.api'
import { MISSING_INFO_CONSTANTS } from '../../constants/missingInfoModal.constants'
import { formatLocalDateWithZone } from '../../utils/date.utils'
import moment from 'moment'
import { MissingInfoPopup } from './MissingInfoPopup'
import { isReadOnlyUser } from '../../utils/permission'
import { startPollingForIntake } from '../../utils/helperFunctions'
import { isEmpty } from 'lodash'

export const MissingInfoIcon = ({
  caseDetails,
  refetchWorkflow,
}: {
  caseDetails: FieldValues
  refetchWorkflow: () => void
}) => {
  const dispatch = useDispatch()
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const workflowTasks = useAppSelector((state) => state?.workflowData?.taskList)
  const revalidateTask: WorkflowInstanceTaskType | undefined =
    workflowTasks?.find((task) => task?.show_on_ui === false)
  const { caseId } = useParams()
  const caseStatusVoidedOrClosed =
    caseDetails?.overallCaseStatus?.toLowerCase() ===
      MISSING_INFO_POPUP_CONSTANTS.CLOSED ||
    caseDetails?.overallCaseStatus?.toLowerCase() === NAMESPACES.VOID
  const hasFetched = useRef(false)
  const [isMiModalOpen, setIsMiModalOpen] = useState<boolean>(false)

  const [totalMissingFields, setTotalMissingFields] = useState<number>(0)
  const [originalMissingData, setOriginaltalMissingData] = useState<DataType[]>(
    []
  )
  const [pendingMiData, setPendingMiData] = useState<DataType[]>([])
  const [resolvedMiData, setResolvedMiData] = useState<DataType[]>([])
  const [overallMiStatus, setOverallMiStatus] =
    useState<OverallMiStatus | null>(null)
  const [miFields, setMiFields] = useState<DropdownType<string>[]>([])

  const toggleMiModal = () => {
    setPendingMiData(originalMissingData)
    setIsMiModalOpen((prev) => !prev)
  }

  const updateCaseState = async (pendingData: DataType[]) => {
    try {
      dispatch(showLoader())
      const hasPending = pendingData.some(
        (item) => item.status === NAMESPACES.PENDING
      )
      if (
        caseDetails &&
        caseDetails?.overallCaseStatus !== NAMESPACES.MISSING_INFORMATION &&
        hasPending
      ) {
        await interruptWorkFlow({
          workflowId: workflowInstance?.id ?? '',
          signal: MISSING_INFO_POPUP_CONSTANTS.MISSING_INFORMATION_INTERRUPT,
        })
        refetchWorkflow()
      } else {
        let saveResult
        if (
          revalidateTask &&
          revalidateTask.state !== communicationStatus.COMPLETED_UPPERCASE
        ) {
          const payload = buildPayload(revalidateTask, true)
          saveResult = await completeTaskByTaskIdApi(payload)
        }
        if (
          (saveResult?.data?.checkerResult?.valid &&
            revalidateTask?.identifier?.stopCurrentFlow) ||
          (caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
            totalMissingFields === 0)
        ) {
          await interruptWorkFlow({
            workflowId: workflowInstance?.id ?? '',
            signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
          })
          refetchWorkflow()
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleSave = async () => {
    try {
      dispatch(showLoader())
      sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
      await updateManualMissingFields(pendingMiData)
      await validateCase(caseId)
      const miDetails = await fetchMissingInformationData(caseId)
      await updateCaseState(miDetails?.data?.pending_missing_info_fields)
      await getMissingInfo(MISSING_INFO_POPUP_CONSTANTS.SAVE_UPPERCASE)
      toggleMiModal()
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleReValidate = async () => {
    try {
      dispatch(showLoader())
      saveModalStateInSession(MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE)
      await updateManualMissingFields(pendingMiData)
      await validateCase(caseId)
      const miDetails = await fetchMissingInformationData(caseId)
      await updateCaseState(miDetails?.data?.pending_missing_info_fields)
      await getMissingInfo(MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE)
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleCancel = async () => {
    sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
    toggleMiModal()
  }
  const buildPayload = (
    task: WorkflowInstanceTaskType | undefined,
    forced = false
  ) => ({
    taskId: task?.id ?? '',
    meta_data: {
      entityId: task?.identifier?.entity_id,
      layoutId: task?.identifier?.layout_info?.layout_id,
    },
    forced,
  })

  const sortByMiStartDate = (
    arr: { mi_start_date: string; mi_end_date: string }[]
  ) => {
    if (!Array.isArray(arr)) return []

    return [...arr].sort((a, b) => {
      const format = 'MM/DD/YYYY HH:mm z'
      const dateA = a.mi_start_date ? moment(a.mi_start_date, format) : null
      const dateB = b.mi_start_date ? moment(b.mi_start_date, format) : null

      if (!dateA && !dateB) return 0
      if (!dateA) return -1
      if (!dateB) return 1

      if (dateA.isBefore(dateB)) return 1
      if (dateA.isAfter(dateB)) return -1
      return 0
    })
  }

  const getMissingInfo = async (type: string) => {
    try {
      const miDetails = await fetchMissingInformationData(caseId)
      // resolved fields
      setResolvedMiData(
        sortByMiStartDate(
          miDetails.data?.resolved_missing_info_fields?.map(
            (item: DataType) => ({
              ...item,
              mi_start_date: formatLocalDateWithZone(
                item?.mi_start_date as string
              ),
              mi_end_date: formatLocalDateWithZone(item?.mi_end_date as string),
              mi_field_type: item?.manually_inserted
                ? MISSING_INFO_POPUP_CONSTANTS.MANUAL
                : MISSING_INFO_POPUP_CONSTANTS.SYSTEM,
            })
          )
        ) as DataTypeStore[]
      )

      // overall MI status
      setOverallMiStatus({
        mi_start_date: miDetails?.data?.mi_start_date,
        mi_end_date: miDetails?.data?.mi_end_date,
        mi_duration: miDetails?.data?.mi_duration,
      })

      const finalArray = miDetails?.data?.pending_missing_info_fields?.map(
        (item: DataType) => ({
          ...item,
          mi_start_date: formatLocalDateWithZone(item?.mi_start_date as string),
          mi_end_date: formatLocalDateWithZone(item?.mi_end_date as string),
          mi_field_type: item?.manually_inserted
            ? MISSING_INFO_POPUP_CONSTANTS.MANUAL
            : MISSING_INFO_POPUP_CONSTANTS.SYSTEM,
        })
      )
      const sortedArray = sortByMiStartDate(finalArray)
      setOriginaltalMissingData(sortedArray)
      setTotalMissingFields(finalArray.length)
      if (
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK ||
        type === MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE ||
        type === MISSING_INFO_POPUP_CONSTANTS.SAVE_UPPERCASE ||
        !hasFetched.current
      ) {
        setPendingMiData(sortedArray)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const saveModalStateInSession = (type: string) => {
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    let parsed
    if (type === MISSING_INFO_POPUP_CONSTANTS.REVALIDATE_UPPERCASE) {
      parsed = {
        openModal: true,
        caseId: caseId,
        manual_mi_fields: [],
      }
    } else if (stored) {
      parsed = JSON.parse(stored)
      if (!parsed.openModal) {
        parsed = { ...parsed, manual_mi_fields: [] }
      }
    }
    if (parsed && parsed.caseId === caseId?.toString()) {
      sessionStorage.setItem(
        MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
        JSON.stringify(parsed)
      )
    }
  }

  const checkMissingInfoWithNoFields = async () => {
    if (
      caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
      totalMissingFields === 0
    ) {
      await interruptWorkFlow({
        workflowId: workflowInstance?.id ?? '',
        signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
      })
      refetchWorkflow()
    }
  }

  const fetchMissingInfoDetails = async ({ type = '' }: { type?: string }) => {
    dispatch(showLoader())
    try {
      saveModalStateInSession(type)
      if (type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK) {
        await checkMissingInfoWithNoFields()
      }
      if (
        !isReadOnlyUser() &&
        !caseStatusVoidedOrClosed &&
        caseDetails &&
        caseDetails?.overallCaseStatus === NAMESPACES.MISSING_INFORMATION &&
        revalidateTask &&
        revalidateTask.state !== communicationStatus.COMPLETED_UPPERCASE &&
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK
      ) {
        const payload = buildPayload(revalidateTask, true)
        await validateCase(caseId)
        const defaultResult = await completeTaskByTaskIdApi(payload)
        if (
          defaultResult?.status === 202 ||
          defaultResult?.data?.instance?.state ===
            WORKFLOW_STATUS.PROCESSING_IN_BACKGROUND
        ) {
          const poolingResponse: FieldValues = await startPollingForIntake({
            caseId: caseId ?? '',
          })
          defaultResult.data = {
            instance: poolingResponse?.workflow_instance,
            preRequisiteExecuted: true,
          }
          if (
            !isEmpty(poolingResponse?.workflow_instance?.background_response)
          ) {
            defaultResult.data.checkerResult =
              poolingResponse?.workflow_instance?.background_response ?? null
          }
        }
        if (
          defaultResult?.data?.checkerResult?.valid &&
          revalidateTask?.identifier?.stopCurrentFlow
        ) {
          await interruptWorkFlow({
            workflowId: workflowInstance?.id ?? '',
            signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
          })

          refetchWorkflow()
        }
      }
      if (
        type === MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK ||
        !hasFetched.current
      ) {
        await getMissingInfo(type)
      }
    } catch (err) {
      console.error(err)
    } finally {
      if (!hasFetched.current) {
        modalBasedOnSessionStorage()
      }
      dispatch(hideLoader())
    }
  }

  const modalBasedOnSessionStorage = () => {
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    if (stored) {
      let parsed = JSON.parse(stored)
      if (parsed.caseId === caseId?.toString()) {
        if (parsed.openModal) {
          setIsMiModalOpen(true)
          if (parsed?.manual_mi_fields.length > 0) {
            setPendingMiData(parsed.manual_mi_fields)
          }
          parsed = { ...parsed, openModal: false }
          sessionStorage.setItem(
            MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
            JSON.stringify(parsed)
          )
          fetchLovs()
          hasFetched.current = true
        }
      } else {
        sessionStorage.removeItem(MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA)
      }
    }
  }

  const handleIconClick = async () => {
    if (workflowDefinition?.global_variables?.new_flow && caseDetails.caseId) {
      await fetchMissingInfoDetails({
        type: MISSING_INFO_POPUP_CONSTANTS.ICON_CLICK,
      })
    }
    setIsMiModalOpen(true)
    fetchLovs()
  }

  const fetchLovs = async () => {
    const result = await getAllLovs()
    setMiFields(
      result?.data?.map(
        (item: {
          mi_field: string
          mi_code: string
          template_label: string
        }) => ({
          ...item,
          label: item.mi_field,
          value: item.mi_field,
        })
      )
    )
  }

  useEffect(() => {
    if (
      workflowDefinition?.global_variables?.new_flow &&
      !!caseDetails.caseId
    ) {
      fetchMissingInfoDetails({
        type: MISSING_INFO_POPUP_CONSTANTS.INITIAL_LOAD,
      })
    }
  }, [caseDetails])

  if (workflowDefinition?.global_variables?.new_flow && caseDetails.caseId) {
    return (
      <>
        <span className="flex ml-[0.625rem] items-center gap-[0.6875rem]">
          <Tooltip
            content={MISSING_INFO_POPUP_CONSTANTS.MISSING_INFORMATION_SUMMARY}
          >
            <button
              className={`flex items-center justify-center w-[1.125rem] h-[1.125rem] text-white rounded-full ${
                totalMissingFields > 0 ? 'bg-[#C8355A]' : 'bg-green-600'
              } font-bold text-xs`}
              type="button"
              onClick={handleIconClick}
            >
              {totalMissingFields}
            </button>
          </Tooltip>
        </span>
        {isMiModalOpen && (
          <AddDataModal>
            <Modal
              isOpen={isMiModalOpen}
              closeModal={toggleMiModal}
              showCloseButton={false}
              width="min-w-[60vw] max-w-[40%]"
              isDragable={true}
            >
              <MissingInfoPopup
                handleCancel={handleCancel}
                setPendingMiData={setPendingMiData}
                pendingMiData={pendingMiData}
                overallMiStatus={overallMiStatus}
                resolvedMiData={resolvedMiData}
                handleSave={handleSave}
                handleReValidate={handleReValidate}
                originalMissingData={originalMissingData}
                miFields={miFields}
                caseStatusVoidedOrClosed={caseStatusVoidedOrClosed}
              />
            </Modal>
          </AddDataModal>
        )}
      </>
    )
  }
}
